// 📄 pages/SAP-BTP/components/DisenosRepositorio.tsx
// Pestaña "Diseños" del Repositorio de iFlows (docs/plans/plan-visor-diagrama-iflows.md,
// E5): bocetos de la organización activa. Crear (en blanco, con un esqueleto
// mínimo), abrir en el editor y ver de qué iFlow salió cada uno.
import React, { useCallback, useEffect, useState } from 'react';
import { GitBranch, PencilRuler, Plus, RefreshCw } from 'lucide-react';
import { ContenidoDiseno, ResumenDiseno, crearDiseno, listarDisenos } from '../../../services/disenoService.ts';
import EditorDiseno from './EditorDiseno.tsx';

interface DisenosRepositorioProps {
  orgId: string;
  archivada: boolean;
  iflowsAdjuntables: { id: string; titulo: string }[];
  // Diseño a abrir (controlado por la página: ej. recién creado desde un iFlow).
  abiertoId: string | null;
  onAbrir: (id: string | null) => void;
  onAviso: (mensaje: string) => void;
}

// Esqueleto de un diseño nuevo: Integration Process con inicio y fin conectados.
const ESQUELETO: ContenidoDiseno = {
  nodos: [
    { id: 'pool_1', tipo: 'pool', subtipo: '', variante: '', nombre: 'Integration Process', x: 200, y: 40, ancho: 600, alto: 260, notas: '' },
    { id: 'inicio_1', tipo: 'inicio', subtipo: 'message', variante: 'MessageStartEvent', nombre: 'Start', x: 240, y: 150, ancho: 32, alto: 32, notas: '' },
    { id: 'fin_1', tipo: 'fin', subtipo: 'message', variante: 'MessageEndEvent', nombre: 'End', x: 720, y: 150, ancho: 32, alto: 32, notas: '' }
  ],
  aristas: [{ id: 'flujo_1', tipo: 'secuencia', desde: 'inicio_1', hasta: 'fin_1', nombre: '', condicion: '', notas: '', adaptador: null }]
};

const fecha = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString('es-CO', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';

export default function DisenosRepositorio({ orgId, archivada, iflowsAdjuntables, abiertoId, onAbrir, onAviso }: DisenosRepositorioProps) {
  const [disenos, setDisenos] = useState<ResumenDiseno[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creando, setCreando] = useState(false);
  const [titulo, setTitulo] = useState('');
  const [ocupado, setOcupado] = useState(false);

  const cargar = useCallback(async () => {
    setError(null);
    try {
      setDisenos(await listarDisenos(orgId));
    } catch (err: any) {
      setError(err?.message || 'No se pudieron cargar los diseños.');
      setDisenos([]);
    }
  }, [orgId]);

  useEffect(() => {
    setDisenos(null);
    cargar();
  }, [cargar]);

  const crear = async () => {
    if (!titulo.trim()) return;
    setOcupado(true);
    setError(null);
    try {
      const r = await crearDiseno(orgId, titulo.trim(), '', ESQUELETO);
      setCreando(false);
      setTitulo('');
      await cargar();
      onAbrir(r.id);
    } catch (err: any) {
      setError(err?.message || 'No se pudo crear el diseño.');
    } finally {
      setOcupado(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        <p className="text-xs text-gray-400">
          Bocetos de iFlows: para pensar un flujo nuevo o una variante de uno existente. No se despliegan; puedes exportarlos a PNG/SVG.
        </p>
        {!archivada && !creando && (
          <button onClick={() => setCreando(true)} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-cyan-600 hover:bg-cyan-500 text-white text-sm font-medium self-start">
            <Plus className="w-4 h-4" /> Nuevo diseño
          </button>
        )}
      </div>

      {creando && (
        <div className="flex flex-col sm:flex-row gap-2 bg-white/5 border border-white/10 rounded-xl p-3">
          <input autoFocus value={titulo} maxLength={120} onChange={(e) => setTitulo(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') crear(); }}
            placeholder="Título del diseño (ej. Órdenes de compra a S/4HANA)"
            className="flex-1 bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500/50" />
          <div className="flex gap-2">
            <button onClick={() => { setCreando(false); setTitulo(''); }} className="px-3 py-2 text-sm text-gray-300 hover:text-white">Cancelar</button>
            <button onClick={crear} disabled={ocupado || !titulo.trim()} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-cyan-600 hover:bg-cyan-500 text-white text-sm font-medium disabled:opacity-40">
              {ocupado && <RefreshCw className="w-4 h-4 animate-spin" />} Crear y abrir
            </button>
          </div>
        </div>
      )}

      {error && <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-xs">{error}</div>}

      {disenos === null ? (
        <div className="p-10 text-center text-gray-400"><RefreshCw className="w-6 h-6 animate-spin mx-auto text-cyan-500" /></div>
      ) : disenos.length === 0 ? (
        <div className="p-10 text-center text-gray-500 bg-white/5 border border-white/10 rounded-2xl">
          <PencilRuler className="w-8 h-8 mx-auto mb-2 text-gray-600" />
          <p className="text-sm">Todavía no hay diseños en esta organización.</p>
          <p className="text-xs mt-1">Crea uno en blanco, o abre un iFlow y usa "Crear boceto" para partir de su diagrama.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {disenos.map((d) => (
            <button key={d.id} onClick={() => onAbrir(d.id)}
              className="text-left bg-white/5 border border-white/10 rounded-2xl p-5 hover:border-cyan-500/40 transition-all flex flex-col gap-2">
              <div className="flex items-start gap-2">
                <PencilRuler className="w-4 h-4 text-cyan-400 mt-0.5 flex-shrink-0" />
                <h3 className="text-white font-medium break-words">{d.titulo}</h3>
              </div>
              {d.descripcion && <p className="text-xs text-gray-400 line-clamp-2">{d.descripcion}</p>}
              {d.origen && (
                <p className="text-[11px] text-gray-400 flex items-center gap-1"><GitBranch className="w-3 h-3" /> A partir de {d.origen.titulo} (rev. {d.origen.version})</p>
              )}
              <p className="text-[11px] text-gray-500 mt-auto pt-2 border-t border-white/5">
                {d.conteos.pasos} paso{d.conteos.pasos === 1 ? '' : 's'} · {d.conteos.conexiones} conexi{d.conteos.conexiones === 1 ? 'ón' : 'ones'}
                {' · '}{d.autorNombre || 'alguien'}{d.actualizadoEn && ` · ${fecha(d.actualizadoEn)}`}
              </p>
            </button>
          ))}
        </div>
      )}

      {abiertoId && (
        <EditorDiseno
          key={abiertoId}
          orgId={orgId}
          disenoId={abiertoId}
          iflowsAdjuntables={iflowsAdjuntables}
          onCerrar={() => onAbrir(null)}
          onCambio={(abrirId) => {
            cargar();
            if (abrirId) onAbrir(abrirId);
          }}
          onAviso={onAviso}
        />
      )}
    </div>
  );
}
